import { CheckIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Separator,
  Text,
} from '@radix-ui/themes';
import {
  getStoredPlateDistanceFt,
  isDistanceCalibrationComplete,
} from 'classes/helpers/vision-install.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { InstallationContext } from 'components/machine/dialogs/installation/context';
import { CommonStepHeader } from 'components/machine/dialogs/installation/header';
import { VisionCalibrationCameraHoC } from 'components/machine/dialogs/vision-calibration/camera/hoc';
import { VisionCalibrationDistanceHoC } from 'components/machine/dialogs/vision-calibration/distance';
import { VisionLowerMachineConfirmation } from 'components/machine/dialogs/vision-calibration/lowered-confirmation';
import { MachineContext } from 'contexts/machine.context';
import { format } from 'date-fns-tz';
import { VisionInstallSubStep } from 'enums/installation';
import { t } from 'i18next';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { LOCAL_VERBOSE_DATETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IVisionCameraCalibration } from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { useContext, useEffect, useMemo, useState } from 'react';
import { VisionCameraCalibrationsService } from 'services/vision-camera-calibrations';
import { useAuthStore } from 'stores/use-auth-store';

export const StepVisionCalibration = () => {
  const current = useAuthStore((s) => s.current);

  const { machine } = useContext(MachineContext);

  const [lastCompletedVcc, setLastCompletedVcc] =
    useState<IVisionCameraCalibration>();
  const {
    installation,
    vcc,
    setVcc,
    updateInstallation,
    setVisionUi,
    visionUi,
  } = useContext(InstallationContext);

  const [subStep, setSubStep] = useState(VisionInstallSubStep.Start);
  const [dialogLowerMachine, setDialogLowerMachine] = useState<{
    onConfirm: () => void;
    key: number;
  }>();

  const distanceInstall = useMemo(
    () => ({
      vcdFt: installation.vcdFt,
      vcdComplete: installation.vcdComplete,
    }),
    [installation.vcdFt, installation.vcdComplete]
  );

  const isDistanceCalibrated = isDistanceCalibrationComplete(
    distanceInstall,
    visionUi.distancePendingComplete
  );
  const isCameraCalibrated = !!vcc?.complete;

  const storedPlateDistanceFt = useMemo(
    () =>
      getStoredPlateDistanceFt(
        distanceInstall,
        MachineHelper.getWheelsInsetFt(machine.hardware_version),
        visionUi.distancePendingComplete
      ),
    [
      distanceInstall,
      machine.hardware_version,
      visionUi.distancePendingComplete,
    ]
  );

  useEffect(() => {
    setVisionUi({ subStep });
  }, [subStep, setVisionUi]);

  const getDateTimeHint = () => {
    if (vcc?.complete) {
      // Current vcc completed -> show current vcc _changed
      return t('common.last-completed-on-x', {
        x: format(new Date(vcc._changed), LOCAL_VERBOSE_DATETIME_FORMAT),
      });
    }

    if (vcc?.result) {
      // Current vcc has results but not completed -> show current vcc _changed as last updated
      return t('common.last-updated-on-x', {
        x: format(new Date(vcc._changed), LOCAL_VERBOSE_DATETIME_FORMAT),
      });
    }

    if (lastCompletedVcc) {
      // Current vcc has no results -> show last completed vcc _changed
      return t('common.last-completed-on-x', {
        x: format(
          new Date(lastCompletedVcc._changed),
          LOCAL_VERBOSE_DATETIME_FORMAT
        ),
      });
    }

    return null;
  };

  useEffect(() => {
    const getLastCompleted = async () => {
      // the last completed VCC may not be from this install session - find the last completed one for info purposes
      const response =
        await VisionCameraCalibrationsService.getInstance().getLastCompleted(
          machine.machineID
        );

      setLastCompletedVcc(response);
    };

    if (vcc?.complete) {
      return;
    }

    getLastCompleted();
  }, [vcc?.complete, machine.machineID]);

  return (
    <ErrorBoundary identifier="StepVisionCalibration">
      {subStep === VisionInstallSubStep.Start && (
        <>
          <CommonStepHeader label="Trajekt Vision Calibration" />

          <Flex direction="column" mt="2" gap="5">
            <Text color={RADIX.COLOR.SECONDARY}>
              To ensure accurate tracking, we'll now calibrate the internal
              stereo camera system. This quick step captures structured images
              and automatically aligns the cameras for optimal performance.
            </Text>
            <Text color={RADIX.COLOR.SECONDARY}>
              Just follow the on-screen guidance — calibration runs
              automatically, and we'll let you know if anything needs adjusting.
            </Text>

            <Card>
              <Grid gap="4" columns="4" align="center">
                {current.role === UserRole.admin && (
                  <>
                    <Flex
                      gridColumn="span 3"
                      direction="column"
                      gap={RADIX.FLEX.GAP.XS}
                    >
                      <Heading size="2" mb="1">
                        Stereo Camera Calibration &nbsp;
                        <Badge
                          color={
                            isCameraCalibrated
                              ? RADIX.COLOR.SUCCESS
                              : RADIX.COLOR.NEUTRAL
                          }
                        >
                          {isCameraCalibrated && <CheckIcon />}
                          {isCameraCalibrated ? 'Completed' : 'Incomplete'}
                        </Badge>
                      </Heading>
                      <Box>
                        <Text color={RADIX.COLOR.SECONDARY}>
                          {t('settings.trajekt-vision-camera-msg')}
                        </Text>
                      </Box>
                      {getDateTimeHint() && (
                        <Box>
                          <Text size="1" style={{ color: 'var(--gray-10)' }}>
                            {getDateTimeHint()}
                          </Text>
                        </Box>
                      )}
                    </Flex>
                    <Box>
                      <Button
                        className="btn-block"
                        variant={RADIX.BUTTON.VARIANT.SECONDARY}
                        onClick={() =>
                          setDialogLowerMachine({
                            key: Date.now(),
                            onConfirm: () =>
                              setSubStep(VisionInstallSubStep.Camera),
                          })
                        }
                      >
                        {t('settings.calibrate')}
                      </Button>
                    </Box>

                    <Box gridColumn="span 4">
                      <Separator size="4" />
                    </Box>
                  </>
                )}

                <Box gridColumn="span 3">
                  <Heading size="2" mb="1">
                    Distance to Plate Calibration &nbsp;
                    <Badge
                      color={
                        isDistanceCalibrated
                          ? RADIX.COLOR.SUCCESS
                          : RADIX.COLOR.NEUTRAL
                      }
                    >
                      {isDistanceCalibrated && <CheckIcon />}
                      {isDistanceCalibrated ? 'Completed' : 'Incomplete'}
                    </Badge>
                  </Heading>
                  <Box>
                    <Text color={RADIX.COLOR.SECONDARY}>
                      {t('settings.trajekt-vision-distance-msg')}
                    </Text>
                  </Box>
                  {isDistanceCalibrated &&
                    machine.last_vision_plate_calibration_changed && (
                      <Text size="1" style={{ color: 'var(--gray-10)' }}>
                        {t('common.last-completed-on-x', {
                          x: format(
                            new Date(
                              machine.last_vision_plate_calibration_changed
                            ),
                            LOCAL_VERBOSE_DATETIME_FORMAT
                          ),
                        })}
                      </Text>
                    )}
                </Box>
                <Box>
                  <Button
                    className="btn-block"
                    variant={RADIX.BUTTON.VARIANT.SECONDARY}
                    onClick={() =>
                      setDialogLowerMachine({
                        key: Date.now(),
                        onConfirm: () =>
                          setSubStep(VisionInstallSubStep.Distance),
                      })
                    }
                  >
                    {t('settings.calibrate')}
                  </Button>
                </Box>
              </Grid>
            </Card>

            {dialogLowerMachine && (
              <VisionLowerMachineConfirmation
                key={dialogLowerMachine.key}
                onConfirm={() => dialogLowerMachine.onConfirm()}
                onClose={() => setDialogLowerMachine(undefined)}
                allowLowering
              />
            )}
          </Flex>
        </>
      )}

      {subStep === VisionInstallSubStep.Camera && (
        <>
          <CommonStepHeader
            label="Stereo Camera Calibration"
            onBack={() => setSubStep(VisionInstallSubStep.Start)}
          />

          {vcc && (
            <VisionCalibrationCameraHoC
              identifier="InstallVCC"
              vccID={vcc._id}
              onVccChange={setVcc}
              onComplete={(finalVcc) => {
                // make sure the install knows about the latest VCC status
                setVcc(finalVcc);

                // make sure the user goes to the distance calibration step next
                setSubStep(VisionInstallSubStep.Distance);
              }}
              verbose
            />
          )}
        </>
      )}

      {subStep === VisionInstallSubStep.Distance && (
        <>
          <CommonStepHeader
            label="Distance to Plate Calibration"
            onBack={() => setSubStep(VisionInstallSubStep.Start)}
          />

          <VisionCalibrationDistanceHoC
            identifier="InstallVCD"
            storedPlateDistanceFt={storedPlateDistanceFt}
            onUpdate={async (position) => {
              const insetFt = MachineHelper.getWheelsInsetFt(
                machine.hardware_version
              );

              const releaseFt = position.py + insetFt;

              await updateInstallation({
                vcdFt: releaseFt,
                vcdComplete: false,
              });
            }}
            onPendingCompleteChange={(pending) =>
              setVisionUi({ distancePendingComplete: pending })
            }
            onComplete={async () => {
              await updateInstallation({ vcdComplete: true });
              setVisionUi({ distancePendingComplete: false });
              setSubStep(VisionInstallSubStep.Start);
            }}
            onCancel={() => setSubStep(VisionInstallSubStep.Start)}
            verbose
          />
        </>
      )}
    </ErrorBoundary>
  );
};
